/**
 * cacheManager.js - Cache management utilities for TimeGenie PWA
 *
 * Provides comprehensive cache management functionality:
 * - Cache clearing and cleanup operations
 * - Service worker update management
 * - Cache health diagnostics and reporting
 * - Smart refresh logic to optimize performance
 * - Force refresh for troubleshooting
 * - Debug logging for development
 */

/**
 * Constants for cache management
 */
export const CACHE_VERSION_PREFIX = 'timegenie-v';
export const MAX_CACHE_COUNT = 20;
export const WARN_CACHE_COUNT = 10;
export const CACHE_DIAG_DATE_FORMAT = 'YYYY-MM-DD'; // For future date formatting

/**
 * @typedef {{ name: string, entries: number }} CacheDetail
 */

/**
 * @typedef {{ scope: string, active: boolean, waiting: boolean, installing: boolean }} ServiceWorkerInfo
 */

/**
 * @typedef {Object} CacheReport
 * @property {string} timestamp
 * @property {boolean} cacheApiSupported
 * @property {boolean} serviceWorkerSupported
 * @property {CacheDetail[]} caches
 * @property {ServiceWorkerInfo|null} serviceWorker
 * @property {string[]} issues
 */

/**
 * CacheManager - Main cache management class
 * Handles all cache-related operations for the PWA
 */
class CacheManager {
  constructor() {
    this.debug = process.env.NODE_ENV === 'development'; // Enable debug logging in development
  }

  /**
   * log - Debug logging with consistent prefix
   * @param {...any} args
   */
  log(...args) {
    if (this.debug) {
      console.log('[Cache Manager]', ...args);
    }
  }

  /**
   * getAllCacheNames - Helper to get all cache names
   * @returns {Promise<string[]>}
   */
  async getAllCacheNames() {
    if (!('caches' in window)) return [];
    return await caches.keys();
  }

  /**
   * deleteCacheByName - Helper to delete a cache by name
   * @param {string} cacheName
   * @returns {Promise<boolean>}
   */
  async deleteCacheByName(cacheName) {
    this.log(`Deleting cache: ${cacheName}`);
    return caches.delete(cacheName);
  }

  /**
   * clearAllCaches - Removes all browser caches for the application
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  async clearAllCaches() {
    try {
      if (!('caches' in window)) {
        this.log('Cache API not supported');
        return false;
      }
      const cacheNames = await this.getAllCacheNames();
      this.log(`Found ${cacheNames.length} caches to clear`);
      await Promise.all(cacheNames.map((name) => this.deleteCacheByName(name)));
      this.log('✅ All caches cleared successfully');
      return true;
    } catch (error) {
      this.log('❌ Error clearing caches:', error);
      return false;
    }
  }

  /**
   * unregisterAllServiceWorkers - Helper to unregister all service workers
   * @returns {Promise<void>}
   */
  async unregisterAllServiceWorkers() {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        this.log('Unregistering service worker:', registration.scope);
        await registration.unregister();
      }
    }
  }

  /**
   * forceRefresh - Performs complete application refresh
   * @returns {Promise<void>}
   */
  async forceRefresh() {
    try {
      await this.clearAllCaches();
      await this.unregisterAllServiceWorkers();
      // Deprecated boolean arg in reload; call without arguments for TS compatibility
      window.location.reload();
    } catch (error) {
      this.log('❌ Error during force refresh:', error);
      window.location.reload();
    }
  }

  /**
   * getCacheDetails - Helper to get cache entry counts
   * @returns {Promise<Array<{name: string, entries: number}>>}
   */
  async getCacheDetails() {
    /** @type {CacheDetail[]} */
    const details = [];
    if (!('caches' in window)) return details;
    const cacheNames = await this.getAllCacheNames();
    for (const cacheName of cacheNames) {
      const cache = await caches.open(cacheName);
      const keys = await cache.keys();
      details.push({ name: cacheName, entries: keys.length });
    }
    return details;
  }

  /**
   * getServiceWorkerInfo - Helper to get SW registration info
   * @returns {Promise<ServiceWorkerInfo|null>}
   */
  async getServiceWorkerInfo() {
    if (!('serviceWorker' in navigator)) return null;
    const registration = await navigator.serviceWorker.getRegistration();
    return registration
      ? /** @type {ServiceWorkerInfo} */ ({
          scope: registration.scope,
          active: !!registration.active,
          waiting: !!registration.waiting,
          installing: !!registration.installing,
        })
      : null;
  }

  /**
   * diagnostics - Analyzes cache health and identifies issues
   * @returns {Promise<CacheReport>}
   */
  async diagnostics() {
    /** @type {CacheReport} */
    const report = {
      timestamp: new Date().toISOString(),
      cacheApiSupported: 'caches' in window,
      serviceWorkerSupported: 'serviceWorker' in navigator,
      caches: /** @type {CacheDetail[]} */ ([]),
      serviceWorker: /** @type {ServiceWorkerInfo|null} */ (null),
      issues: /** @type {string[]} */ ([]),
    };
    try {
      report.caches = await this.getCacheDetails();
      report.serviceWorker = await this.getServiceWorkerInfo();
      // Analyze issues
      if (!report.cacheApiSupported) report.issues.push('Cache API not supported');
      if (!report.serviceWorkerSupported) report.issues.push('Service Worker not supported');
      if (report.caches.length > MAX_CACHE_COUNT) {
        report.issues.push('Extreme number of caches - fatal cache state');
      } else if (report.caches.length > WARN_CACHE_COUNT) {
        report.issues.push('Too many caches - potential cleanup needed');
      }
      const today = new Date().toISOString().split('T')[0] || '';
      const oldCaches = report.caches.filter(
        (cache) => cache.name.includes(CACHE_VERSION_PREFIX) && !cache.name.includes(today),
      );
      if (oldCaches.length > 0) {
        report.issues.push(`${oldCaches.length} potentially stale caches found`);
      }
      this.log('Cache diagnostics:', report);
      return report;
    } catch (error) {
      this.log('❌ Error during diagnostics:', error);
      const msg = error instanceof Error ? error.message : String(error);
      report.issues.push(`Diagnostics failed: ${msg}`);
      return report;
    }
  }

  /**
   * updateServiceWorker - Updates service worker and handles activation
   * @returns {Promise<boolean>} True if update successful, false otherwise
   */
  async updateServiceWorker() {
    try {
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker not supported');
      }
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        throw new Error('No service worker registration found');
      }
      this.log('Checking for service worker updates...');
      await registration.update();
      if (registration.waiting) {
        this.log('Activating waiting service worker...');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        return new Promise((resolve) => {
          navigator.serviceWorker.addEventListener(
            'controllerchange',
            () => {
              this.log('✅ Service worker updated and activated');
              resolve(true);
            },
            { once: true },
          );
        });
      }
      this.log('Service worker is up to date');
      return true;
    } catch (error) {
      this.log('❌ Service worker update failed:', error);
      return false;
    }
  }

  /**
   * smartRefresh - Intelligent cache refresh based on diagnostics
   * @returns {Promise<boolean>} True if refresh was performed, false if not needed
   */
  async smartRefresh() {
    try {
      const diagnostics = await this.diagnostics();
      const needsRefresh = diagnostics.issues.some(
        (issue) => issue.includes('stale') || issue.includes('cleanup'),
      );
      if (needsRefresh) {
        this.log('🔄 Smart refresh triggered - clearing stale caches');
        await this.clearAllCaches();
        await this.updateServiceWorker();
        return true;
      }
      this.log('✅ Cache is healthy - no refresh needed');
      return false;
    } catch (error) {
      this.log('❌ Smart refresh failed:', error);
      return false;
    }
  }
}

// Export singleton instance for use throughout the application
export const cacheManager = new CacheManager();

// Export utility functions for convenient access to cache operations
// These functions delegate to the singleton instance methods
export const clearAllCaches = () => cacheManager.clearAllCaches();
export const forceRefresh = () => cacheManager.forceRefresh();
export const diagnostics = () => cacheManager.diagnostics();
export const updateServiceWorker = () => cacheManager.updateServiceWorker();
export const smartRefresh = () => cacheManager.smartRefresh();

export default cacheManager;
